import { Box, Button, Checkbox, IconButton, Typography } from '@mui/material';
import { Outlet, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CustomCellRendererProps } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { LayerApiModel, WithSymbol, WithTemplate } from '../../../types/models/layer.model';
import {
  AutodeskLayerApiModel,
  AutodeskLayerRelationship,
  WithLineweight,
  WithPlotStyle,
  WithLayer,
} from '../../../types/models/autodesk-layer.model';
import { useConfirm } from '../contexts/confirm.context';
import { InfniteScrollingGrid } from '../components/AgGrid/InfiniteScrollingGrid';
import { searchAutodeskLayers } from '../api/autodesk-layers.api';
import { useDeleteAutodeskLayer } from '../hooks/api/use-autodesk-layers.hook';

export const AutodeskLayers = () => {
  const navigate = useNavigate();
  const [showConfirm] = useConfirm();

  const [deleteAutodeskLayer, { loading: deletingAutodeskLayer, error: deleteAutodeskLayerError }] =
    useDeleteAutodeskLayer();

  const columns: ColDef<
    AutodeskLayerApiModel & WithSymbol & WithTemplate & WithPlotStyle & WithLineweight & WithLayer
  >[] = [
    {
      field: 'name',
      colId: 'layer.name',
      valueGetter: ({ data }) => data?.layer.name,
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'template',
      colId: 'layer.template.name',
      valueGetter: ({ data }) => data?.layer?.template?.name,
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'color',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'symbol',
      colId: 'layer.symbolLinetype.name',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
      headerName: 'Linetype',
      valueGetter: ({ data }) => data?.layer?.symbol.name,
    },
    {
      field: 'lineweight',
      colId: 'lineweight.weight',
      filter: 'agNumberColumnFilter',
      filterParams: {
        filterOptions: ['equals'],
        maxNumConditions: 1,
      },
      sortable: true,
      valueGetter: ({ data }) => data?.lineweight.weight,
    },
    {
      field: 'isPlot',
      filter: true,
      sortable: true,
      headerName: 'Plot',
      cellDataType: 'boolean',
      filterParams: {
        values: [true, false],
        valueFormatter: (params: { value: boolean }) => (params.value ? 'Yes' : 'No'),
      },
      cellRenderer: ({ node }: CustomCellRendererProps<AutodeskLayerApiModel>) => (
        <Checkbox checked={node.data?.isPlot} disabled />
      ),
    },
    {
      field: 'plotStyle',
      colId: 'plotStyle.name',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
      headerName: 'Plot Style',
      valueGetter: ({ data }) => data?.plotStyle?.name,
    },
    {
      field: 'transparency',
      filter: true,
      sortable: true,
      filterParams: {
        filterOptions: ['equals'],
        maxNumConditions: 1,
      },
    },
    {
      field: 'isNewViewportFreeze',
      headerName: 'New VP Freeze',
      cellDataType: 'boolean',
      filter: true,
      sortable: true,
      filterValueGetter: ({ data }) => (data?.isNewViewportFreeze ? 'Yes' : 'No'),
      cellRenderer: ({ node }: CustomCellRendererProps<AutodeskLayerApiModel>) => (
        <Checkbox checked={node.data?.isNewViewportFreeze} disabled />
      ),
    },
    {
      headerName: 'Actions',
      minWidth: 200,
      maxWidth: 200,
      cellRenderer: ({ api, node }: CustomCellRendererProps<AutodeskLayerApiModel & WithLayer>) => (
        <>
          <IconButton onClick={() => navigate(`edit/${node.id}`)}>
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              showConfirm({
                onConfirm: async () => {
                  if (node.id) {
                    await deleteAutodeskLayer({ autodeskLayerId: +node.id });
                    api.refreshInfiniteCache();
                  }
                },
                title: 'Delete CAD Layer',
                message: `Are you sure you want to delete CAD layer "${(node.data?.layer as LayerApiModel).name}"?`,
                error: deleteAutodeskLayerError?.response?.data?.message,
              });
            }}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const getAutodeskLayers: Parameters<typeof InfniteScrollingGrid<AutodeskLayerApiModel>>[0]['onGetRows'] = async ({
    cursor,
    limit,
    filterQuery,
    filterModel,
    sortQuery,
  }) => {
    const { data } = await searchAutodeskLayers({
      include: [
        AutodeskLayerRelationship.LAYER,
        AutodeskLayerRelationship.LINEWEIGHT,
        AutodeskLayerRelationship.COLOR_BOOK,
        AutodeskLayerRelationship.PLOT_STYLE,
        AutodeskLayerRelationship.SYMBOL,
        AutodeskLayerRelationship.TEMPLATE,
      ],
      cursor,
      limit,
      sort: sortQuery,
      ...filterQuery,
      isNewViewportFreeze: filterModel?.isNewViewportFreeze?.type,
      isPlot: filterModel?.isPlot?.type,
    });

    return data;
  };

  return (
    <Box sx={{ height: '100%' }}>
      <Outlet />
      <Helmet title="CAD Layers" />
      <Box display="flex" flexDirection="column" sx={{ height: '100%', p: '0 12px' }}>
        <Box display="flex" sx={{ m: '10px 0' }}>
          <Typography variant="h5">CAD Layers</Typography>
          <Box flexGrow={1} />
          <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`add`)}>
            Add Layer
          </Button>
        </Box>

        <Box alignSelf={'stretch'} flexGrow={1} sx={{ pb: '12px' }}>
          <InfniteScrollingGrid
            loading={deletingAutodeskLayer}
            columnDefs={columns}
            onGetRows={getAutodeskLayers}
            localeText={{
              true: 'Yes',
              false: 'No',
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
